import Color from '@/Enums/Color.enum';
import type { IBaseAudienceTargeting } from '@/Interfaces/Audiences/Audience.interface';
import type { IAudienceControlOption } from '@/Interfaces/ControlOption.interface';
import AudienceTargetingService from '@/Services/AudienceTargeting.service';
import SearchInput from '@/components/SearchInput';
import useListSearch from '@/hooks/useListSearch';
import { Box, Checkbox, FormControlLabel, FormGroup, Skeleton, SxProps, Theme } from '@mui/material';
import { useCallback, useMemo } from 'react';

interface IProps<V> {
  options: IAudienceControlOption<V>[];
  value: IBaseAudienceTargeting<IAudienceControlOption<V>>;
  onChange: (newValue: IAudienceControlOption<V>, checked: boolean) => void;
  loading?: boolean;
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

const AudienceList = <V extends number | string>({ options, onChange, value, loading, disabled, sx }: IProps<V>) => {
  const { search, searchTerm, onSearchValueChange } = useListSearch();

  const displayedOptions = useMemo(() => {
    if (!searchTerm.length) {
      return options;
    }
    return options.filter(option => option.label?.toLowerCase().includes(searchTerm));
  }, [searchTerm, options]);

  const selectedOptionValues = useMemo(
    () => AudienceTargetingService.getAudienceIdSetFromTargetingControlOptions(value),
    [value],
  );

  const excludesGroups = useMemo(
    () =>
      new Set(
        (value?.include ?? [])
          .flat()
          .map(item => item.excludesGroup?.id)
          .filter(Boolean) ?? [],
      ),
    [value?.include],
  );
  const groups = useMemo(
    () =>
      new Set(
        (value?.include ?? [])
          .flat()
          .flatMap(item => item?.groups?.map(group => group?.id))
          .filter(Boolean) ?? [],
      ),
    [value?.include],
  );

  const isOptionChecked = useCallback((value: V) => selectedOptionValues.has(value), [selectedOptionValues]);

  const isItemDisabled = useCallback(
    (option: IAudienceControlOption<V>) => {
      if (!option?.groups?.length) {
        return false;
      }
      const isInExcludesGroups = option?.groups?.some(item => excludesGroups.has(item.id));
      const isInGroups = groups.has(option.excludesGroup?.id);
      return isInExcludesGroups || isInGroups;
    },
    [excludesGroups, groups],
  );

  return (
    <>
      <SearchInput
        disabled={loading || disabled}
        sx={{ displayPrint: 'none', width: '100%' }}
        value={search}
        onChange={onSearchValueChange}
        placeholder='Search Audiences'
      />
      <Box
        sx={{
          width: '100%',
          maxHeight: '300px',
          border: '1px solid',
          padding: '10px',
          borderRadius: '6px',
          borderColor: Color.GreyBorder,
          overflowY: 'auto',
          ...sx,
        }}
      >
        {loading ? (
          <Skeleton variant='rectangular' height='100%' />
        ) : (
          <FormGroup>
            {displayedOptions?.length ? (
              displayedOptions.map(option => {
                const checked = isOptionChecked(option.value);
                const exlcuded = !checked && isItemDisabled(option);

                return (
                  <FormControlLabel
                    disabled={exlcuded || disabled}
                    sx={{
                      margin: 0,
                      displayPrint: !checked ? 'none' : 'inherit',
                      '&:hover': {
                        borderRadius: '4px',
                        backgroundColor: 'rgba(27, 67, 50, 0.10)',
                      },
                    }}
                    key={option.value}
                    control={<Checkbox checked={checked} onChange={() => onChange(option, !checked)} />}
                    label={option.label}
                  />
                );
              })
            ) : (
              <div>No options</div>
            )}
          </FormGroup>
        )}
      </Box>
    </>
  );
};

export default AudienceList;
